import { db } from '@app/icare/client/db'
import type { ResultTypeFromQueryResult } from '@app/icare/client/db/client/localQueryOptions'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { queryKit } from '@app/icare/db/query/queryKit'
import { entityRegistry } from '@app/icare/entities/registry'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { mergeNonEmptyStrings } from '@pkg/shared/utils'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainerRuntime'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Divider, Space, Table, Typography } from 'antd'
import { sql } from 'kysely'
import { jsonArrayFrom } from 'kysely/helpers/sqlite'
import React from 'react'
import { FaCircle } from 'react-icons/fa'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/adteam/')({
  component: RouteComponent,
})

const Filter = QueryBaseSchema.extend({
  sortBy: z.enum(['name']).nullish(),
})

const filter = createQueryStore({
  schema: Filter,
})

const { label } = entityRegistry.adTeam
function RouteComponent() {
  const [open, setOpen] = React.useState(false)
  const f = filter(useShallow(s => s))

  const adTeamParams = queryKit.parseQuery(f, { paginationMode: 'offset' })
  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('adTeam')
        .selectAll()
        .select(eb => [
          jsonArrayFrom(
            eb
              .selectFrom('user')
              .select(['user.firstName', 'user.lastName', 'user.id'])
              .whereRef('user.roles', 'like', sql`'%' || ${sql.ref('adTeam.entraGroupName')} || '%'`),
          ).as('members'),
        ])
        .$call(queryKit.deleted(f.deleted))
        .$call(queryKit.all(adTeamParams)),
      params: adTeamParams,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'adTeam.name',
        title: label,
        width: '200px',
        fixed: 'start',
        render: (_, row) => <Typography.Text>{row.name}</Typography.Text>,
      },
      {
        key: 'members',
        title: <Trans>Betreuer</Trans>,
        sorter: false,
        render: (_, row) => (
          <Space separator={<FaCircle size={4} className="text-gray-400 mx-1" />} wrap size={0}>
            {(row.members || []).map(c => (
              <Link key={c.id} to={`/mitarbeiter/$id`} params={{ id: c.id }}>
                {mergeNonEmptyStrings([c.firstName, c.lastName])}
              </Link>
            ))}
          </Space>
        ),
      },
      {
        key: 'actions',
        title: <Trans>Aktionen</Trans>,
        sorter: false,
        fixed: 'end',
        render: (_, row) => (
          <ButtonLink to="/adteam/$id" params={{ id: row.id }} size="small" shape="round">
            <Trans>Anzeigen</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    filter,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput value={f.meta.searchRawValue} onChange={e => f.setSearch(e.target.value)} />
            <FilterButton activeFilterCount={f.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
        footer: <Divider size="small" style={{ marginBottom: 0 }} />,
      },
    },
    [f.meta.searchRawValue, f.setSearch, f.meta.activeFilterCount, setOpen, f.meta.activeFilterCount],
  )
  return (
    <QueryFilterWrapper queryStore={filter} open={open} onOpenChange={setOpen}>
      <Table
        className="min-w-full"
        dataSource={data?.data ?? []}
        loading={isFetching}
        columns={columns}
        rowKey="id"
        pagination={data}
        onChange={f.onChange}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </QueryFilterWrapper>
  )
}
